import React, { useMemo, useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { useMainPerformanceProductCategories, SectionProps } from '../../hooks/useMainPerformanceSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor, mergeChartOptions } from '../../utils/themeHelpers';
import ChartControls from '../ui/ChartControls';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface ProductCategorizationProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const ProductCategorization: React.FC<ProductCategorizationProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: productCategoryData, isLoading, isError, error, refetch } = useMainPerformanceProductCategories(branchId, timeRange);
  
  // Component metadata
  const componentId = 'product-categorization';
  const componentTitle = 'توزیع فروش دسته‌بندی محصولات (درصد)';

  // Calculate insights from the data
  const insights = useMemo(() => {
    if (!productCategoryData || productCategoryData.length === 0) return null;

    // Sort categories by value
    const sortedCategories = [...productCategoryData].sort((a, b) => (b.value || 0) - (a.value || 0));
    
    // Top 3 categories
    const topCategories = sortedCategories.slice(0, 3);
    const topCategoriesTotal = topCategories.reduce((sum, cat) => sum + (cat.value || 0), 0);
    
    // Categories with less than 5% share
    const smallCategories = sortedCategories.filter(cat => (cat.value || 0) < 5);
    
    // Calculate concentration ratio (how much top 3 contribute)
    const concentrationRatio = topCategoriesTotal;
    
    // Find dominant category if any (>40%)
    const dominantCategory = sortedCategories.find(cat => (cat.value || 0) > 40);
    
    // Calculate diversity score (inverse of concentration)
    const diversityScore = 100 - concentrationRatio;

    return {
      topCategories,
      topCategoriesTotal,
      smallCategories,
      concentrationRatio,
      dominantCategory,
      diversityScore,
      totalCategories: productCategoryData.length
    };
  }, [productCategoryData]);

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'} animate-enter animate-delay-3`}>
        <div className="card-base card-padding">
          <div className="flex flex-col items-center justify-center h-64">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری دسته‌بندی محصولات…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <ErrorMessage 
            message="خطا در دریافت اطلاعات دسته‌بندی محصولات"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className={`${compact ? 'col-span-1' : 'col-span-1'} card-base card-padding animate-enter animate-delay-3`}>
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.productCategories)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'توزیع فروش بر اساس دسته‌بندی محصولات',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
            companions: ['customer-retention-analysis']
          }
        })}
        isFavorited={isFavorited(componentId)}
        className="mb-4"
        chartRef={chartRef}
      />
      <div className="mb-6">
        <h3 className="title-card">{componentTitle}</h3>
        
        {productCategoryData && productCategoryData.length > 0 && (
          <div className="text-xs text-text-tertiary mt-2">
            تعداد کل دسته‌بندی‌ها: {productCategoryData.length} | 
            نمایش: {Math.min(productCategoryData.length, 8)} مورد اول
          </div>
        )}
      </div>
      <div style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار دایره‌ای دسته‌بندی محصولات">
        {productCategoryData && productCategoryData.length > 0 ? (
          (() => {
            const displayData = productCategoryData.slice(0, 8);
            
            return (
              <Pie
                data={{
                  labels: displayData.map(item => item.name || item.Name || item.category || item.CategoryName || 'نامشخص'),
                  datasets: [
                    {
                      data: displayData.map(item => item.value || 0),
                      backgroundColor: displayData.map((_, index) => 
                        getChartBackgroundColor(index, currentTheme, 0.8)
                      ),
                      borderColor: displayData.map((_, index) => 
                        getChartColors(currentTheme)[index % getChartColors(currentTheme).length]
                      ),
                      borderWidth: 2,
                    }
                  ]
                }}
                options={mergeChartOptions(getChartJsOptions(currentTheme), {
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      display: false
                    },
                    y: {
                      display: false
                    }
                  },
                  plugins: {
                    legend: {
                      // Any pie-specific legend overrides can go here
                    },
                    tooltip: {
                      enabled: true,
                      external: undefined,
                      position: 'nearest',
                      intersect:true,
                      callbacks: {
                        label: function(context: any) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const separator = '\u00A0:\u00A0';
                          const formattedValue = `${value}%`;
                          return `${label}${separator}${formattedValue}`;
                        },
                        title: function(context: any) {
                          return context[0]?.label || '';
                        }
                      }
                    }
                  }
                })}
              />
            );
          })()
        ) : (
          <EmptyState />
        )}
      </div>
      
      {/* Insights Section */}
    
      {/* Show expandable list for all categories if there are any */}
      {productCategoryData && productCategoryData.length > 0 && (
        <div className="mt-6">
          <details className="cursor-pointer">
            <summary className="text-sm text-text-secondary hover:text-text-primary transition-colors font-medium cursor-pointer">
              مشاهده کامل دسته بندی ها
            </summary>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {productCategoryData.map((category, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 p-3 bg-bg-secondary rounded-lg hover:bg-bg-tertiary transition-colors"
                >
                  <div 
                    className="w-4 h-4 rounded-full border-2 flex-shrink-0"
                    style={{ 
                      backgroundColor: getChartBackgroundColor(index % 8, currentTheme, 0.8),
                      borderColor: getChartColors(currentTheme)[index % getChartColors(currentTheme).length]
                    }}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {category.name || category.Name || category.category || category.CategoryName || 'نامشخص'}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {category.value || 0}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default ProductCategorization;